import { useState } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Integrations from "@/components/Integrations";
import Features from "@/components/Features";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/* =======================================================
   TIPOS E VARIANTES DOS PLANOS
======================================================= */

type PlanVariant = {
  id: string;
  price: string;
  users: number;
  flows: number;
  executions: string;
  storage: string;
  label: string;
};

const basicVariants: PlanVariant[] = [
  {
    id: "b700",
    price: "R$ 700,00",
    users: 3,
    flows: 20,
    executions: "10.000",
    storage: "250 MB",
    label: "R$ 700,00 • 10.000 execuções",
  },
  {
    id: "b900",
    price: "R$ 900,00",
    users: 5,
    flows: 30,
    executions: "15.000",
    storage: "500 MB",
    label: "R$ 900,00 • 15.000 execuções",
  },
];

const proVariants: PlanVariant[] = [
  {
    id: "p1200",
    price: "R$ 1.200,00",
    users: 7,
    flows: 50,
    executions: "20.000",
    storage: "1 GB",
    label: "R$ 1.200,00 • 20.000 execuções",
  },
  {
    id: "p3000",
    price: "R$ 3.000,00",
    users: 10,
    flows: 100,
    executions: "30.000",
    storage: "2 GB",
    label: "R$ 3.000,00 • 30.000 execuções",
  },
  {
    id: "p5000",
    price: "R$ 5.000,00",
    users: 20,
    flows: 150,
    executions: "40.000",
    storage: "3 GB",
    label: "R$ 5.000,00 • 40.000 execuções",
  },
  {
    id: "p7000",
    price: "R$ 7.000,00",
    users: 30,
    flows: 200,
    executions: "50.000",
    storage: "4 GB",
    label: "R$ 7.000,00 • 50.000 execuções",
  },
];

/* =======================================================
   LISTAS DE FUNCIONALIDADES GERADAS DINAMICAMENTE
======================================================= */

const getBasicFeatures = (v: PlanVariant) => [
  `${v.flows} fluxos ativos`,
  `${v.executions} execuções`,

  `${v.storage} de armazenamento`,
  "Permissionamento",
  "2 Workspaces",
  "45 minutos de tempo de execução",
  "Suporte por email e WhatsApp",
  "SLA de 8 horas úteis",
  "Log de 15 dias",
  "Assistente de IA integrado no fluxo",
  "Nodes básicos e PRO",
];

const getProFeatures = (v: PlanVariant) => [
  `${v.flows} fluxos ativos`,
  `${v.executions} execuções`,
  "3H de consultoria inclusas",
  `${v.storage} de armazenamento`,
  "10 Workspaces",
  "Controle de versão no Git",
  "60 minutos de tempo de execução",
  "Suporte por email, WhatsApp e ligação",
  "SLA de 4 horas úteis",
  "Log de 30 dias",
  "RAG (vector + knowledge graph)",
  "Nodes básicos, PRO e Premium",
];

const Index = () => {
  const [basicId, setBasicId] = useState<string>("b700");
  const [proId, setProId] = useState<string>("p1200");

  const basicActive = basicVariants.find((v) => v.id === basicId)!;
  const proActive = proVariants.find((v) => v.id === proId)!;

  const WHATSAPP_PHONE = "5527996259361";

  function buildWhatsAppUrl(message: string) {
    const text = encodeURIComponent(message);
    return `https://wa.me/${WHATSAPP_PHONE}?text=${text}`;
  }

  function openWhatsApp(message: string) {
    window.open(buildWhatsAppUrl(message), "_blank");
  }

  function basicMessage(v: PlanVariant) {
    return [
      "Olá! Tenho interesse em assinar o AscFlow.",
      "",
      "Plano: Básico",
      `Opção: ${v.label}`,
      `Preço: ${v.price}/mês`,
      `Incluso: ${v.flows} fluxos • ${v.executions} execuções • ${v.storage} armazenamento`,
      "",
      "Pode me passar os próximos passos para contratação?",
    ].join("\n");
  }

  function proMessage(v: PlanVariant) {
    return [
      "Olá! Tenho interesse em assinar o AscFlow.",
      "",
      "Plano: Profissional",
      `Opção: ${v.label}`,
      `Preço: ${v.price}/mês`,
      `Incluso: ${v.flows} fluxos • ${v.executions} execuções • ${v.storage} armazenamento`,
      "",
      "Pode me passar os próximos passos para contratação?",
    ].join("\n");
  }

  function starterMessage() {
    return [
      "Olá! Tenho interesse em assinar o AscFlow.",
      "",
      "Plano: Inicial",
      "Preço: R$ 350,00/mês",
      "Incluso: 5 fluxos • 1.000 execuções • 100 MB • 15 min execução",
      "",
      "Pode me passar os próximos passos para contratação?",
    ].join("\n");
  }

  function customMessage() {
    return [
      "Olá! Tenho interesse no AscFlow.",
      "",
      "Plano: Custom (sob consulta)",
      "Quero um plano sob medida (fluxos/execuções/storage/SLA).",
      "",
      "Pode me chamar para alinharmos a necessidade e orçamento?",
    ].join("\n");
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <Hero />
      <Integrations />
      <Features />

      {/* ================= PLANOS E PREÇOS ================= */}
      <section
        id="planos"
        className="py-28 bg-gradient-to-b from-transparent to-primary/5"
      >
        <div className="container mx-auto px-4 lg:px-8">
          {/* Título */}
          <div className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-gradient">Planos e Preços</span>
            </h2>
            <p className="text-2xl text-muted-foreground max-w-3xl mx-auto">
              Escolha o plano ideal para sua empresa
            </p>
          </div>

          {/* ==================== GRID DE PLANOS ==================== */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* ==================== PLANO INICIAL ==================== */}
            <div className="p-8 rounded-3xl border border-primary/30 bg-card/50 backdrop-blur-sm hover:scale-[1.03] transition-all">
              <h3 className="text-2xl font-bold text-accent text-center mb-6">
                Plano Inicial
              </h3>

              <p className="text-center text-4xl font-extrabold leading-tight">
                R$ 350,00
              </p>
              <p className="text-center text-muted-foreground text-sm mb-4">
                /mês
              </p>

              <ul className="space-y-3 mb-6 text-base">
                {[
                  "5 fluxos ativos",
                  "1.000 execuções",
                  "100 MB de armazenamento",
                  "15 minutos de tempo de execução",
                  "Suporte por email",
                  "SLA de 24 horas úteis",
                  "Log de 7 dias",
                  "Nodes básicos",
                ].map((f, i) => (
                  <li key={i} className="flex space-x-3 items-start">
                    <Check className="text-accent w-5 h-5 mt-1" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full"
                variant="outline"
                onClick={() => openWhatsApp(starterMessage())}
              >
                ASSINAR AGORA
              </Button>
            </div>

            {/* ==================== PLANO BÁSICO ==================== */}
            <div className="p-8 rounded-3xl border border-primary/30 bg-card/50 backdrop-blur-sm hover:scale-[1.03] transition-all">
              <h3 className="text-2xl font-bold text-accent text-center mb-6">
                Plano Básico
              </h3>

              <p className="text-center text-4xl font-extrabold leading-tight mt-1">
                {basicActive.price}
              </p>
              <p className="text-center text-sm text-muted-foreground -mt-1 mb-4">
                {basicActive.executions} execuções
              </p>

              <Select value={basicId} onValueChange={setBasicId}>
                <SelectTrigger
                  className="
                    w-full mb-4 px-4 py-3 rounded-xl
                    bg-black/20
                    border-transparent
                    text-sm text-foreground
                    shadow-[0_0_18px_rgba(120,255,200,0.15)]
                    backdrop-blur-xl
                    flex justify-between items-center
                  "
                >
                  <SelectValue
                    placeholder="Selecione a opção"
                    defaultValue="b700"
                  />
                </SelectTrigger>

                <SelectContent
                  className="
                    bg-[#07040f]
                    border border-accent/40
                    rounded-xl shadow-2xl
                    p-2
                    min-w-[250px]
                    text-sm
                  "
                >
                  {basicVariants.map((v) => (
                    <SelectItem
                      key={v.id}
                      value={v.id}
                      className="
                        group cursor-pointer px-3 py-2
                        rounded-lg
                        flex items-center justify-between
                        data-[state=checked]:bg-accent/20
                      "
                    >
                      <span className="font-medium text-foreground group-hover:opacity-80 transition-opacity">
                        {v.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <ul className="space-y-3 mb-6 text-base">
                {getBasicFeatures(basicActive).map((f, i) => (
                  <li key={i} className="flex space-x-3 items-start">
                    <Check className="text-accent w-5 h-5 mt-1" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full"
                onClick={() => openWhatsApp(basicMessage(basicActive))}
              >
                ASSINAR AGORA
              </Button>
            </div>

            {/* ==================== PLANO PROFISSIONAL ==================== */}
            <div className="relative p-8 rounded-3xl border border-accent bg-accent/10 backdrop-blur-sm hover:scale-[1.03] transition-all">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-background px-5 py-1 rounded-full text-sm font-bold shadow-md">
                Mais Popular
              </div>

              <h3 className="text-2xl font-bold text-accent text-center mb-6">
                Plano Premium
              </h3>

              <p className="text-center text-4xl font-extrabold leading-tight mt-1">
                {proActive.price}
              </p>
              <p className="text-center text-sm text-muted-foreground -mt-1 mb-4">
                {proActive.executions} execuções
              </p>

              <Select value={proId} onValueChange={setProId}>
                <SelectTrigger
                  className="
                    w-full mb-4 px-4 py-3 rounded-xl
                    bg-black/20
                    border-transparent
                    text-sm text-foreground
                    shadow-[0_0_20px_rgba(0,255,200,0.35)]
                    backdrop-blur-xl
                    flex justify-between items-center
                  "
                >
                  <SelectValue
                    placeholder="Selecione a opção"
                    defaultValue="p1200"
                  />
                </SelectTrigger>

                <SelectContent
                  className="
                    bg-[#030303]
                    border border-accent/40
                    rounded-xl shadow-2xl
                    p-2
                    min-w-[250px]
                    text-sm
                  "
                >
                  {proVariants.map((v) => (
                    <SelectItem
                      key={v.id}
                      value={v.id}
                      className="
                        group cursor-pointer px-3 py-2
                        rounded-lg
                        flex items-center justify-between
                        data-[state=checked]:bg-accent/20
                      "
                    >
                      <span className="font-medium text-foreground group-hover:opacity-80 transition-opacity">
                        {v.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <ul className="space-y-3 mb-6 text-base">
                {getProFeatures(proActive).map((f, i) => (
                  <li key={i} className="flex space-x-3 items-start">
                    <Check className="text-accent w-5 h-5 mt-1 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full bg-gradient-to-r from-primary to-secondary"
                onClick={() => openWhatsApp(proMessage(proActive))}
              >
                ASSINAR AGORA
              </Button>
            </div>

            {/* ==================== PLANO CUSTOM ==================== */}
            <div className="p-8 rounded-3xl border border-primary/30 bg-card/50 backdrop-blur-sm hover:scale-[1.03] transition-all">
              <h3 className="text-2xl font-bold text-accent text-center mb-6">
                Plano Custom
              </h3>

              <p className="text-center text-4xl font-extrabold leading-tight">
                Sob consulta
              </p>
              <p className="text-center text-muted-foreground text-sm mb-4">
                Plano sob medida
              </p>

              <ul className="space-y-3 mb-10 text-base">
                {[
                  "Fluxos ilimitados",
                  "Execuções ilimitadas",
                  "Armazenamento dedicado",
                  "SLA personalizado",
                  "Suporte dedicado",
                  "Workspaces ilimitados",
                  "Recursos exclusivos",
                ].map((f, i) => (
                  <li key={i} className="flex space-x-3 items-start">
                    <Check className="text-accent w-5 h-5 mt-1" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                className="w-full"
                variant="outline"
                onClick={() => openWhatsApp(customMessage())}
              >
                FALE CONOSCO
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
